(() => {
  const root = document.querySelector('.ac-samba-map-page');
  const schools = window.ACSambaSchools;

  if (!root || !Array.isArray(schools) || !window.L) return;

  const detail = root.querySelector('[data-map-detail]');
  const intro = root.querySelector('[data-map-intro]');
  const fields = root.querySelector('[data-map-fields]');
  const logo = root.querySelector('[data-map-logo]');
  const name = root.querySelector('[data-map-name]');
  const division = root.querySelector('[data-map-division]');
  const directions = root.querySelector('[data-map-directions]');
  const storyLink = root.querySelector('[data-map-story]');
  const close = root.querySelector('[data-map-close]');
  const count = root.querySelector('.ac-samba-map-panel__count');
  const groupButtons = [...root.querySelectorAll('[data-map-group]')];
  const divisionButtons = groupButtons.filter((button) => button.dataset.mapGroup !== 'all');
  const allDivisions = divisionButtons.map((button) => button.dataset.mapGroup);
  const selectedDivisions = new Set(allDivisions);
  const empty = root.querySelector('[data-map-empty]');
  const source = root.querySelector('[data-map-source]');
  const map = L.map('ac-samba-map', {
    zoomControl: true,
    minZoom: 8,
    maxZoom: 19,
  }).setView([-22.87, -43.3], 10);
  let activeMarker = null;
  const schoolMarkers = [];

  L.tileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}',
    {
      maxNativeZoom: 16,
      maxZoom: 19,
      attribution: 'Tiles &copy; Esri',
    }
  ).addTo(map);

  L.tileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}',
    {
      maxNativeZoom: 16,
      maxZoom: 19,
      pane: 'overlayPane',
    }
  ).addTo(map);

  const safeLink = (url) => {
    try {
      const parsed = new URL(url, window.location.origin);
      return ['http:', 'https:'].includes(parsed.protocol) ? parsed.href : '';
    } catch (_) {
      return '';
    }
  };

  const rows = [
    ['Founded', 'founded'],
    ['Address', 'address'],
    ['Current division', 'division'],
    ['Status', 'status'],
    ['Titles won', 'titles'],
    ['Colours', 'colours'],
    ['Instagram', 'instagram', 'instagramUrl'],
    ['Website', 'website', 'websiteUrl'],
    ['Rainha de bateria', 'queen'],
    ['Carnival director', 'director'],
    ['Carnavalesco', 'carnavalesco'],
  ];

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const markerSummary = (school) => {
    const foundingYear = school.foundedYear || String(school.founded || '').match(/(?:18|19|20)\d{2}/)?.[0];
    return `
    <strong class="ac-samba-map-marker-card__name">${escapeHtml(school.name)}</strong>
    <span><b>Founded:</b> ${escapeHtml(foundingYear || 'Not documented')}</span>
    <span><b>Years active:</b> ${escapeHtml(school.activeYears || 'Not documented')}</span>
  `;
  };

  const setActiveMarker = (marker) => {
    if (activeMarker?._icon) activeMarker._icon.classList.remove('is-active');
    activeMarker = marker;
    if (activeMarker?._icon) activeMarker._icon.classList.add('is-active');
    if (activeMarker) activeMarker.setZIndexOffset(1000);
  };

  const showSchool = (school, marker) => {
    setActiveMarker(marker);
    intro.hidden = true;
    detail.hidden = false;
    logo.hidden = !school.logo;
    if (school.logo) {
      logo.src = school.logo;
      logo.alt = `${school.name} crest`;
    } else {
      logo.removeAttribute('src');
      logo.alt = '';
    }
    name.textContent = school.name;
    division.textContent = school.division;
    directions.href = safeLink(school.maps) || '#';
    directions.textContent = school.historicalLocation
      ? 'Open historical address in Google Maps'
      : 'Open in Google Maps';
    storyLink.hidden = !school.historicalLocation;
    storyLink.onclick = school.historicalLocation
      ? () => document.dispatchEvent(new CustomEvent('ac:samba:open-school', { detail: { id: school.id } }))
      : null;
    fields.replaceChildren();

    rows.forEach(([label, valueKey, linkKey]) => {
      const value = school[valueKey];
      if (!value) return;

      const row = document.createElement('div');
      const term = document.createElement('dt');
      const description = document.createElement('dd');
      const link = linkKey ? safeLink(school[linkKey]) : '';

      row.className = 'ac-samba-map-detail__row';
      if (valueKey === 'address' && school.historicalLocation) {
        term.textContent = 'Historical quadra address';
      } else if (valueKey === 'division' && school.historicalLocation) {
        term.textContent = 'Directory category';
      } else {
        term.textContent = label;
      }

      if (link) {
        const anchor = document.createElement('a');
        anchor.href = link;
        anchor.target = '_blank';
        anchor.rel = 'noopener';
        anchor.textContent = value;
        description.append(anchor);
      } else {
        description.textContent = value;
      }

      row.append(term, description);
      fields.append(row);
    });
  };

  const bounds = [];

  schools.forEach((school) => {
    const initials = school.name.split(/\s+/).filter(Boolean).slice(0, 2).map((word) => word[0]).join('').toUpperCase();
    const markerContent = school.logo
      ? `<img class="ac-samba-logo-marker__image" src="${escapeHtml(school.logo)}" alt="">`
      : `<span class="ac-samba-logo-marker__fallback" aria-hidden="true">${escapeHtml(initials)}</span>`;
    const icon = L.divIcon({
      className: 'ac-samba-logo-marker',
      html: markerContent,
      iconSize: [52, 52],
      iconAnchor: [26, 46],
      popupAnchor: [0, -46],
    });
    const marker = L.marker([school.lat, school.lng], {
      icon,
      title: school.name,
      alt: school.name,
      riseOnHover: true,
    });

    marker.bindTooltip(
      `<span class="ac-samba-map-marker-tooltip">${markerSummary(school)}</span>`,
      { direction: 'top', offset: [0, -42] }
    );
    marker.bindPopup(`
      <div class="ac-samba-map-marker-card">
        ${markerSummary(school)}
        ${school.historicalLocation ? `<button type="button" data-map-open-dossier="${school.id}">Open dossier</button>` : ''}
      </div>
    `, { minWidth: 220, maxWidth: 300, closeButton: true });
    marker.on('click', () => showSchool(school, marker));
    schoolMarkers.push({ school, marker });
    bounds.push([school.lat, school.lng]);
  });

  const updateMap = () => {
    const visibleBounds = [];
    let visibleCount = 0;

    schoolMarkers.forEach(({ school, marker }) => {
      const schoolGroups = school.mapGroups?.length ? school.mapGroups : [school.division];
      const isVisible = schoolGroups.some((group) => selectedDivisions.has(group));

      if (isVisible) {
        marker.addTo(map);
        visibleBounds.push([school.lat, school.lng]);
        visibleCount += 1;
      } else if (map.hasLayer(marker)) {
        map.removeLayer(marker);
      }
    });

    groupButtons.forEach((button) => {
      const buttonGroup = button.dataset.mapGroup;
      const isActive = buttonGroup === 'all'
        ? selectedDivisions.size === allDivisions.length
        : selectedDivisions.has(buttonGroup);
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
    });

    const selectedLabels = allDivisions.filter((group) => selectedDivisions.has(group));
    const groupMetadata = {
      'Grupo B': {
        message: 'This legacy LIVRES category ended after Carnival 2023. Markers may represent the last documented Grupo B location of a now-inactive school.',
        source: 'https://galeriadosamba.com.br/escolas-de-samba/grupo-b/',
      },
      'Grupo de Avaliação': {
        message: 'Only schools with a public location that could be independently verified are mapped. Private registrations and unverified venues are omitted.',
        source: 'https://galeriadosamba.com.br/escolas-de-samba/grupo-f/',
      },
      'Inativas ou Extintas': {
        message: 'These markers show verified historical quadra addresses, not necessarily current venues. Profiles without a defensible numbered address are omitted.',
        source: 'https://galeriadosamba.com.br/escolas-de-samba/inativas-ou-extintas/',
      },
    };
    const selectedMetadata = selectedLabels.length === 1 ? groupMetadata[selectedLabels[0]] : null;

    if (selectedLabels.length === allDivisions.length) {
      count.textContent = `${visibleCount} mapped schools and historical locations across Greater Rio de Janeiro`;
    } else if (selectedLabels.length === 1) {
      count.textContent = `${visibleCount} mapped schools or historical locations in ${selectedLabels[0]}`;
    } else {
      count.textContent = `${visibleCount} mapped schools in ${selectedLabels.length} selected groups`;
    }

    const message = selectedMetadata?.message
      || (visibleCount === 0 ? 'No schools in the selected groups currently have a verified mappable location.' : '');
    empty.textContent = message;
    empty.hidden = !message;
    source.hidden = !selectedMetadata?.source;
    source.href = selectedMetadata?.source || '#';

    if (visibleBounds.length) {
      map.fitBounds(visibleBounds, { padding: [45, 45], maxZoom: 11 });
    } else {
      map.setView([-22.87, -43.3], 10);
    }

    detail.hidden = true;
    intro.hidden = false;
    setActiveMarker(null);
  };

  groupButtons.forEach((button) => {
    button.addEventListener('click', () => {
      const group = button.dataset.mapGroup;

      if (group === 'all') {
        selectedDivisions.clear();
        allDivisions.forEach((divisionName) => selectedDivisions.add(divisionName));
      } else if (selectedDivisions.size === allDivisions.length) {
        selectedDivisions.clear();
        selectedDivisions.add(group);
      } else if (selectedDivisions.size === 1 && selectedDivisions.has(group)) {
        selectedDivisions.clear();
        allDivisions.forEach((divisionName) => selectedDivisions.add(divisionName));
      } else if (selectedDivisions.has(group)) {
        selectedDivisions.delete(group);
      } else {
        selectedDivisions.add(group);
      }

      updateMap();
    });
  });

  updateMap();

  map.on('popupopen', (event) => {
    const button = event.popup.getElement()?.querySelector('[data-map-open-dossier]');
    if (!button) return;
    button.addEventListener('click', () => {
      document.dispatchEvent(new CustomEvent('ac:samba:open-school', {
        detail: { id: Number(button.dataset.mapOpenDossier) },
      }));
    }, { once: true });
  });

  close.addEventListener('click', () => {
    detail.hidden = true;
    intro.hidden = false;
    if (activeMarker?._icon) activeMarker._icon.classList.remove('is-active');
    activeMarker = null;
  });
})();
